import React from 'react';
import { Empty, Flex, Input, Pagination, Typography, theme } from 'antd';
import { ChargeBeePlanId } from '@prerender/billing-shared';

import { useSearchPerformancePlanLabel } from '../SearchPerformanceAccess';
import SearchPerformanceDomainCapCounter from '../SearchPerformanceDomainCapCounter';
import SearchPerformanceUpgradeCta, { SEARCH_PERFORMANCE_PLAN_COPY } from '../SearchPerformanceUpgradeCta';
import { GSC_PICKER_CAP_HELP_AT_CAP } from './GscActivationErrors';
import GscPickerAccountLine from './GscPickerAccountLine';
import GscPropertyPickerRow from './GscPropertyPickerRow';
import { GSC_DOMAIN_PICKER_PAGE_SIZE, type GscDomainPicker } from './useGscDomainPicker';

const SEARCH_PLACEHOLDER = 'Find domain';
const NO_MATCHES = 'No properties found.';

type Props = {
  picker: GscDomainPicker;
  upgradeCtaLocation: string;
  accountEmail?: string | null;
  syncedUrls?: string[];
  /**
   * Freeze rows while a save is in flight: the diff was captured at click, so a
   * mid-flight toggle would be discarded.
   */
  disabled?: boolean;
};

function GscPropertyPickerList({ picker, upgradeCtaLocation, accountEmail, syncedUrls, disabled = false }: Props) {
  const { token } = theme.useToken();
  const planLabel = useSearchPerformancePlanLabel();

  return (
    <Flex vertical flex={1} gap={token.margin} style={{ minWidth: 0 }}>
      {accountEmail && <GscPickerAccountLine email={accountEmail} />}
      <Input.Search
        size="large"
        placeholder={SEARCH_PLACEHOLDER}
        aria-label={SEARCH_PLACEHOLDER}
        allowClear
        value={picker.query}
        onChange={(e) => picker.setQuery(e.target.value)}
      />
      <div>
        <SearchPerformanceDomainCapCounter
          used={picker.selected.length}
          max={picker.maxDomains}
          planLabel={planLabel}
          helpAtCap={GSC_PICKER_CAP_HELP_AT_CAP}
        />
      </div>
      {picker.canUpsellDomainCap && (
        <Flex align="center" justify="space-between" gap={token.marginSM} wrap>
          <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM, flex: 1, minWidth: 0 }}>
            {SEARCH_PERFORMANCE_PLAN_COPY.proAtCap}
          </Typography.Text>
          <SearchPerformanceUpgradeCta
            type="link"
            targetPlanId={ChargeBeePlanId.EnterprisePlus}
            ctaLocation={upgradeCtaLocation}
            ctaName={SEARCH_PERFORMANCE_PLAN_COPY.upgradeToEnterprise}
          >
            {SEARCH_PERFORMANCE_PLAN_COPY.upgradeToEnterprise}
          </SearchPerformanceUpgradeCta>
        </Flex>
      )}
      {picker.filteredCount === 0 ? (
        <Empty description={NO_MATCHES} />
      ) : (
        <Flex vertical gap={token.margin}>
          {picker.paginated.map((property) => {
            const isLostUnlisted = picker.lostUnlistedUrls.includes(property.siteUrl);
            const checked = isLostUnlisted
              ? picker.retainedLostUrls.includes(property.siteUrl)
              : picker.selected.includes(property.siteUrl);
            const isUnsyncable = property.isPermissionSufficient === false;
            const isDomainBlocked = !checked && !isLostUnlisted && picker.isSameDomainBlocked(property.siteUrl);
            const isCapBlocked = !checked && !isLostUnlisted && picker.atLimit;
            return (
              <GscPropertyPickerRow
                key={property.siteUrl}
                property={property}
                checked={checked}
                accessLost={
                  isLostUnlisted ? 'unlisted' : picker.isRelistedLost(property.siteUrl) ? 'relisted' : undefined
                }
                disabled={
                  disabled ||
                  isDomainBlocked ||
                  isCapBlocked ||
                  // Unsyncable additions stay blocked; a checked committed row
                  // must remain toggleable so it can be removed.
                  (isUnsyncable && !checked)
                }
                isUnsyncable={isUnsyncable}
                isDomainBlocked={isDomainBlocked}
                isCapBlocked={isCapBlocked}
                isSynced={syncedUrls?.includes(property.siteUrl) ?? false}
                onToggle={() => picker.toggle(property.siteUrl)}
              />
            );
          })}
        </Flex>
      )}
      {picker.filteredCount > GSC_DOMAIN_PICKER_PAGE_SIZE && (
        <Flex justify="flex-end" style={{ marginTop: token.marginSM }}>
          <Pagination
            current={picker.page}
            pageSize={GSC_DOMAIN_PICKER_PAGE_SIZE}
            total={picker.filteredCount}
            showSizeChanger={false}
            onChange={picker.setPage}
          />
        </Flex>
      )}
    </Flex>
  );
}

export default GscPropertyPickerList;
